import currencyFormatter from 'currency-formatter';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { businessAdminRole, invitationStatus, inviteType, routes, userRoles } from '../../../../../constants';
import { ConfirmationModal, ControlGroup, ControlOption, FileUploadFormField, Icon, InputFormField } from '../../../../../containers/components';
import { handleInvalidForm, isBusinessAdmin, isCustomSubscription, notCanceled, validateEmail } from '../../../../../services';
import { businessActions, businessSelector } from '../../../../../state/modules/business';
import { paymentActions } from '../../../../../state/modules/payment';
import { noCapFilter, noCapFind, noCapMap, pluralise } from '../../../../../utils';
import Loading from '../../../loading';

const MAX_INIT_INVITATION_ROWS = 10;

const TeamInvitations = ({
  business,
  cancelAction,
  cancelText,
  confirmText,
  displayExistingInvitations,
  exceededCount,
  csvExceededCount,
  csvExtraCharge,
  extraCharge,
  handleHoverRemove,
  interval,
  invitations,
  csvSummary,
  invite,
  submitForm,
  maxAllowedInvitations,
  maxInvExceeded,
  csvMaxInvExceeded,
  onInvitationChanges,
  updateInvitationCount,
  existingPendingInvitations,
  teamMemberCount,
  remainingInvitationCount,
  inviteOption,
  onInviteOptionChanges,
  onCsvDrop,
  csvFile,
  uploadValidation,
  displayConfirmationModal,
  hideConfirmationModal,
  invitationLink,
  setTextRef,
  copyToClipboard,
  isLinkCopied,
  postConfirmAction,
  currentSubscription,
  maxInvReached
}) => (
    <Grid>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <h1 className="text-center">Invite your team</h1>
          <p>
            {
              remainingInvitationCount > 0
              && [`Invite your remaining ${remainingInvitationCount} team ${pluralise('member', remainingInvitationCount)} below.`, <br key="br" />]
            }
            You can add or remove users from your team in your account settings at any time.
          </p>
          {
            !displayExistingInvitations && existingPendingInvitations.length > 0 ?
              <p>
                Your business already sent out {existingPendingInvitations.length} pending {pluralise('invitation', existingPendingInvitations.length)}.
              </p> :
              null
          }
          {
            teamMemberCount > 1 ?
              <p>
                Your business currently has {teamMemberCount} {pluralise('user', teamMemberCount)}.
              </p> :
              null
          }
        </Col>
      </Row>
      <Row className='inviteMembers'>
        <form noValidate onSubmit={(e) => submitForm(e)}>
          <ControlGroup
            id='content-option'
            control='radio'
            type='pill'
            label={null}
            onChange={(e) => onInviteOptionChanges(e)}>
            <ControlOption value={inviteType.Manual} checked={inviteOption === inviteType.Manual} type={'radio'} label={<span>Manual</span>} />
            <ControlOption value={inviteType.BulkUpload} checked={inviteOption === inviteType.BulkUpload} type={'radio'} label={<span>Bulk upload</span>} />
            <ControlOption value={inviteType.SignUpLink} checked={inviteOption === inviteType.SignUpLink} type={'radio'} label={<span>Sign up link</span>} />
          </ControlGroup>
          <fieldset>
            {
              inviteOption === inviteType.BulkUpload ?
                <Row className='sm-gutter'>
                  {
                    csvMaxInvExceeded ?
                      <Row className='sm-gutter'>
                        <Col xs={12}>
                          {
                            isCustomSubscription(currentSubscription) ?
                              <div className='warning grey'>
                                <Icon icon='warning-large-alt red' />
                                Your current account is set up for {business.subscription.quantity} users (including yourself).
                                Please contact us if you want to add more users.
                              </div> :
                              <div className='warning grey'>
                                <Icon icon='warning-large-alt red' />
                                Your current account is set up for {business.subscription.quantity} users (including yourself).
                                By adding {csvExceededCount} extra user(s) you'll be charged an additional {csvExtraCharge} per {interval}.
                              </div>
                          }
                        </Col>
                      </Row> : null
                  }
                  <p>Note: For CSV uploads, please map the columns of your file properties to 'Email Address', 'First Name', 'Last Name'.</p>
                  <p>Click <a href="/templates/bulkUpload.csv">here</a> to download a sample CSV file </p>
                  <FileUploadFormField
                    styleName='file'
                    accept='.csv'
                    multiple={false}
                    onFileDrop={onCsvDrop}
                    file={csvFile}
                    validationMessage={uploadValidation}
                    dropLabel='Upload CSV file' />
                  {
                    csvSummary ?
                      <div>
                        <p>{csvSummary.total} users loaded from file</p>
                        {
                          csvSummary.invalidEmailNum > 0 || csvSummary.existingInvitationNum > 0 ?
                            <p className='alert alert-danger'>
                              {csvSummary.invalidEmailNum > 0 ? `${csvSummary.invalidEmailNum} invalid emails: ${getInvalidInvites(csvSummary.invalidEmailInvitations)}` : ``} <br />
                              {csvSummary.existingInvitationNum > 0 ? `${csvSummary.existingInvitationNum} existing invitations: ${getInvalidInvites(csvSummary.existingInvitations)}` : ``}
                            </p>
                            : null
                        }
                      </div> : null
                  }
                </Row>
                : null
            }
            {
              inviteOption === inviteType.Manual ?
                noCapMap((invitation, key) =>
                  <div key={key}>
                    {
                      key === maxAllowedInvitations && maxInvExceeded ?
                        <Row className='sm-gutter'>
                          <Col xs={12}>
                            <div className='warning grey'>
                              <Icon icon='warning-large-alt red' />
                              Your current account is set up for {business.subscription.quantity} users (including yourself).
                              By adding {exceededCount} extra user(s) you'll be charged an additional {extraCharge} per {interval}.
                            </div>
                          </Col>
                        </Row> :
                        null
                    }
                    {
                      (!displayExistingInvitations && fp.isEmpty(invitation.status)) || displayExistingInvitations ?
                        <Row className={invitation.removeHovered ? 'sm-gutter hovered' : 'sm-gutter'}>
                          {
                            fp.isEmpty(invitation.status) ?
                              <Button bsStyle='plain'
                                onClick={() => updateInvitationCount(key, -1)}
                                className='remove'
                                onMouseEnter={(e) => handleHoverRemove(e, key, invitations)}
                                onMouseLeave={(e) => handleHoverRemove(e, key, invitations)}>
                                <Icon icon='close-thick' />
                              </Button> :
                              null
                          }
                          <label className='rowTitle'>
                            Team member {key + 1}
                          </label>
                          <Col xs={12} md={4}>
                            {
                              /*
                                Disable the email address field to prevent edit and sending more invitations,
                                invitation resend and edit will be done on manage invitation page.
                              */
                            }
                            <InputFormField {...emailInputProps(key, invitation, onInvitationChanges)} />
                          </Col>
                          <Col xs={12} md={4}>
                            <InputFormField {...firstNameInputProps(key, invitation, onInvitationChanges)} />
                          </Col>
                          <Col xs={12} md={4}>
                            <InputFormField {...lastNameInputProps(key, invitation, onInvitationChanges)} />
                          </Col>
                        </Row> :
                        null
                    }
                  </div>
                )(invitations) : null
            }
            {
              inviteOption === inviteType.Manual ?
                (!isCustomSubscription(currentSubscription) || !maxInvReached) ?
                  <Row className='sm-gutter'>
                    <Col xs={12}>
                      <p>
                        <Button className='addRow' bsStyle='plain' onClick={() => updateInvitationCount(null, 1)}>
                          <span className='quantity-up'>+</span> <span>Add another User</span>
                        </Button>
                      </p>
                    </Col>
                  </Row> :
                  null :
                null
            }
            <Row className='sm-gutter submit-row'>
              {inviteOption === inviteType.SignUpLink ?
                remainingInvitationCount > 0 ?
                  [
                    <p key="copy">Copy the link below and send it to anyone who you would like to join your Caddie account.</p>,
                    <Col key="text-link" xs={12} md={5} mdOffset={3}>
                      <InputFormField id="invite-link" inputProps={{ ref: (input) => setTextRef(input), readOnly: true }} value={invitationLink} />
                      {
                        isLinkCopied ? <span className='help-message'>Copied to clipboard</span> : null
                      }
                    </Col>,
                    <Col key="copy-button" xs={12} md={1}>
                      <button className="btn btn-default sm-full btn-slim" onClick={copyToClipboard}>Copy</button>
                    </Col>
                  ] :
                  <p>You need to increase the number of user licenses before you can add any more users via a sign up link. You can add more user licenses <a href={routes.app.accountSettings('payment')}>here</a>. </p>
                : null
              }
              <Col xs={12} md={6} mdOffset={3}>
                {inviteOption === inviteType.SignUpLink && postConfirmAction ?
                  <Button className='large pull-down-single full' onClick={postConfirmAction}>
                    Close
                  </Button> :
                  isCustomSubscription(currentSubscription) && csvMaxInvExceeded ?
                    <p key='cancelAction'>
                      <Button bsStyle='plain' onClick={cancelAction}>
                        {cancelText}
                      </Button>
                    </p> :
                    [<Button key='confirmInvite' type='submit' className='large' disabled={disableSubmit(inviteOption, invitations, csvSummary)}>
                      {confirmText}
                    </Button>,
                    <p key='cancelAction'>
                      <Button bsStyle='plain' onClick={cancelAction}>
                        {cancelText}
                      </Button>
                    </p>]
                }
              </Col>
            </Row>
          </fieldset>
        </form>
      </Row>
      {
        csvSummary ?
          <ConfirmationModal
            show={displayConfirmationModal}
            actions={[{
              onConfirmAction: invite,
              actionLabel: 'Yes',

            }]}
            cancel={hideConfirmationModal}
            message={<p>You are about to invite {csvSummary.total} {pluralise('user', csvSummary.total)} to join your Caddie account. Would you like to proceed?</p>}
            headingText={<span>Please confirm your invitations</span>}
            cancelLabel={`No, take me back`} /> : null
      }
    </Grid>
  );

// The max user allowed to be invited has to exclude the BusinessAdmin.
const maxAllowedInv = (currentSubscription, teamMembers) => fp.max([currentSubscription.quantity - teamMembers.length, 0]);

const existingInvitations = fp.map(({
  email,
  firstName,
  lastName,
  status
}) => ({
  email,
  firstName,
  lastName,
  invalidEmail: false,
  status
}));

const remainingAllowedInvitationCount = (maxInvitationCount, existingInvitations) =>
  fp.max([maxInvitationCount - existingInvitations.length, 0]);

const remainingInvitations = (remainingAllowedUser) =>
  fp.fill(0, remainingAllowedUser, {
    email: '',
    firstName: '',
    lastName: '',
    invalidEmail: false
  }, Array(remainingAllowedUser));

const addOrRemoveInvitations = (key, count, invitations) =>
  count < 0 ?
    noCapFilter((_, index) => index !== key)(invitations) :
    [
      ...invitations,
      {
        email: '',
        firstName: '',
        lastName: '',
        invalidEmail: false
      }
    ];

const isInvitationValid = ({ email, invalidEmail }) =>
  !fp.isEmpty(email.trim()) && !invalidEmail;

const inputProps = (id, alternativeLabel) => (key, value, onChange) => ({
  id: `${fp.kebabCase(id)}-${key}`,
  value: fp.get(id)(value),
  label: alternativeLabel ? alternativeLabel : fp.upperFirst(id),
  onChange: (e) => onChange(id, e.target.value, key),
  inputProps: {
    maxLength: 255,
    name: `${fp.kebabCase(id)}-${key}`,
    disabled: fp.get('status')(value)
  }
});

const firstNameInputProps = inputProps('firstName', 'First Name');
const lastNameInputProps = inputProps('lastName', 'Last Name');
const emailInputProps = (key, {
  invalidEmail,
  hasExistingPendingInvite,
  status,
  ...value
}, onChange) => ({
  ...inputProps('email')(key, {
    ...value,
    invalidEmail,
    hasExistingPendingInvite,
    status
  }, onChange),
  label: status ?
    <span>
      Email
      &nbsp;
      <span className='text-success'>
        <Icon icon='accept' />
        &nbsp;
        {status}
      </span>
    </span> :
    'Email',
  validation: invalidEmail ?
    'Please provide valid email address.' :
    hasExistingPendingInvite ?
      'Email address has existing pending invitation.' :
      ''
});

const pendingInvitations = fp.filter(
  ({ status }) => !status ||
    status === invitationStatus.Sent ||
    status === invitationStatus.Created
);

const disableSubmit = (inviteOption, invitations, csvSummary) => {
  if (inviteOption === inviteType.Manual) {
    return !fp.flow(
      fp.filter(inv => fp.isEmpty(inv.status)),
      fp.some(({ email }) => !fp.isEmpty(email.trim()))
    )(invitations)
  }
  else if (inviteOption === inviteType.BulkUpload) {
    return !(csvSummary && fp.isEmpty(csvSummary.invalidEmailInvitations) && fp.isEmpty(csvSummary.existingInvitations))
  }
  else {
    return false
  }
}


const NoValidSubscription = () => (
  <div id='no-valid-subscription-message'>
    <h2 className='text-center'>
      You cannot invite users as your subscription is inactive.
    </h2>
  </div>
);

const InivtationLoader = () => (
  <div className='invitation-loader'>
    <Loading />
  </div>
);

const parseCsv = (content, existingPendingInvitations) => {
  let rows = content.split("\n");
  let firstRow = rows[0].split(",").map(fp.trim);
  let validRows = validateEmail(firstRow[0]) ? rows : fp.tail(rows);
  let contentList = fp.map((row) => {
    let fields = row.split(",").map(fp.trim);
    return {
      email: fields[0],
      firstName: fields[1],
      lastName: fields[2],
      invalidEmail: fields[0] !== '' && !validateEmail(fields[0]),
      hasExistingPendingInvite: fp.flow(
        fp.map(({ email }) => email),
        fp.includes(fields[0])
      )(existingPendingInvitations)
    }
  }
  )(validRows)
  return contentList
};

const getCsvSummary = (invitations) => {
  const invalidEmailInvitations = fp.filter(({ invalidEmail }) => invalidEmail)(invitations);
  const existingInvitations = fp.filter(({ hasExistingPendingInvite }) => hasExistingPendingInvite)(invitations);

  return {
    total: invitations.length,
    invalidEmailNum: invalidEmailInvitations.length,
    existingInvitationNum: existingInvitations.length,
    invalidEmailInvitations,
    existingInvitations
  }
};

const setExtraCharge = ({
  unitPrice: {
    currencyCode: code,
    minorAmount
  }
}, exceededCount) => currencyFormatter.format(
  minorAmount * exceededCount / 100,
  { code }
);

const getInvalidInvites = (invalidInvites) => fp.flow(
  fp.map((inv) => inv.email),
  fp.join(', '),
  invalidInvites.length > 10 ?
    (emails) => emails.concat(`, and ${invalidInvites.length - 10} more.`) :
    fp.identity
)(invalidInvites.slice(0, 10))

const mapStateToProps = (state) => ({
  config: state.configuration,
  user: state.user,
  business: state.business,
  businessId: fp.get('id')(businessSelector(state)),
  loadingInvitations: state.business.loadingInvitations,
  currentSubscription: notCanceled(state.business.subscription),
  selectedPlan: fp.find(
    ({ id }) => id === state.configuration.stripeConfig.plansInUse.SmallTeam
  )(state.payment.plans),
  invitationLink: state.business.invitationLink
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  getPlans: bindActionCreators(paymentActions.payment.getPlans, dispatch),
  sendInvitations: bindActionCreators(businessActions.business.sendInvitations, dispatch),
  getBusinessSubscription: bindActionCreators(businessActions.business.getSubscription, dispatch),
  updateBusinessSubscription: bindActionCreators(businessActions.business.updateSubscription, dispatch),
  getInvitationLink: bindActionCreators(businessActions.business.getInvitationLink, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({
    business: {
      subscription,
      teamMembers,
      invitations
    },
    selectedPlan
  }) => ({
    interval: fp.getOr('month')('interval.interval')(selectedPlan),
    existingPendingInvitations: pendingInvitations(invitations),
    teamMemberCount: teamMembers.length,
    maxAllowedInvitations: maxAllowedInv(subscription, teamMembers)
  })),
  withProps(({
    existingPendingInvitations,
    business: {
      subscription,
      teamMembers
    },
  }) => ({
    remainingInvitationCount: maxAllowedInv(subscription, teamMembers) - existingPendingInvitations.length
  })),
  withStateHandlers(
    {
      invitations: [],
      csvFileInvitations: [],
      csvSummary: null,
      maxInitialInvitationRows: MAX_INIT_INVITATION_ROWS,
      touchedSubscription: false,
      maxInvExceeded: false,
      csvMaxInvExceeded: false,
      invitationInited: false,
      currentSubQuantity: 0,
      csvFileSubQuantity: 0,
      inviteOption: inviteType.Manual,
      displayConfirmationModal: false,
      textRef: null,
      isLinkCopied: false
    },
    {
      onInvitationChanges: ({ invitations }) => (field, value, key) => ({
        invitations: noCapMap(
          (inv, index) => index === key ?
            {
              ...inv,
              [field]: value
            } :
            inv
        )(invitations)
      }),
      setExtraChargeState: () => (selectedPlan, exceededCount) => ({
        extraCharge: setExtraCharge(selectedPlan, exceededCount)
      }),
      setValidInvitations: () => (validatedInvitations) => ({
        invitations: validatedInvitations
      }),
      handleHoverRemove: () => (e, key, invitations) => ({
        invitations: noCapMap((inv, index) =>
          index === key ?
            {
              ...inv,
              removeHovered: e.type === 'mouseenter'
            } :
            inv
        )(invitations)
      }),
      setInitInvitations: () => (invitations, currentSubQuantity) => ({
        invitations: invitations,
        currentSubQuantity: currentSubQuantity,
        invitationInited: true
      }),
      setTouchSubscription: () => () => ({
        touchedSubscription: true
      }),
      updateInvitations: () => (
        key,
        count,
        invitations,
        maxInvExceeded,
        exceededCount,
        currentSubQuantity
      ) => ({
        invitations: addOrRemoveInvitations(key, count, invitations),
        maxInvExceeded,
        exceededCount,
        currentSubQuantity
      }),
      onInviteOptionChanges: () => (e) => ({
        inviteOption: e.target.value,
        isLinkCopied: false
      }),
      onCsvParsed: (_, { existingPendingInvitations, maxAllowedInvitations, teamMemberCount, selectedPlan }) => (fileContent) => {
        const inviteList = parseCsv(fileContent, existingPendingInvitations);
        const csvSummary = getCsvSummary(inviteList);
        const pendingInvitationCount = existingPendingInvitations.length + inviteList.length;
        return {
          csvFileInvitations: inviteList,
          csvSummary,
          csvMaxInvExceeded: pendingInvitationCount > maxAllowedInvitations,
          csvExceededCount: pendingInvitationCount - maxAllowedInvitations,
          csvFileSubQuantity: pendingInvitationCount + teamMemberCount,
          csvExtraCharge: setExtraCharge(selectedPlan, pendingInvitationCount - maxAllowedInvitations)
        }
      },
      showConfirmationModal: () => (e) => {
        e.preventDefault();
        e.stopPropagation();
        return {
          displayConfirmationModal: true
        }
      },
      hideConfirmationModal: () => () => ({
        displayConfirmationModal: false
      }),
      setTextRef: () => (ref) => ({ textRef: ref }),
      copyInvitationLink: () => () => ({ isLinkCopied: true })
    }
  ),
  withStateHandlers(
    {
      isParsingCsv: false,
      csvFile: null,
      uploadValidation: null
    },
    {
      onCsvDrop: (_, { onCsvParsed }) => (acceptedFiles, rejectedFiles) => {
        const acceptedFile = fp.first(acceptedFiles);
        const rejectedFile = fp.first(rejectedFiles);

        if (acceptedFile) {
          var reader = new FileReader();
          reader.onload = () => {
            const fileContent = reader.result
            onCsvParsed(fileContent)
          }
          reader.readAsText(acceptedFile);
          return {
            csvFile: acceptedFile,
            uploadValidation: null,
          }
        }

        if (rejectedFile) {
          return {
            csvFile: null,
            uploadValidation: 'This file cannot be read'
          }
        }
      }
    }
  ),
  withProps(
    ({
      invitations,
      maxAllowedInvitations
    }) => {
      const currentInvCount = pendingInvitations(invitations).length
      return {
        maxInvReached: currentInvCount >= maxAllowedInvitations
      }
    }
  ),
  withHandlers({
    updateInvitationCount: ({
      selectedPlan,
      teamMemberCount,
      invitations,
      currentSubQuantity,
      setExtraChargeState,
      updateInvitations,
      maxAllowedInvitations
    }) => (key, count) => {
      const currentInvCount = pendingInvitations(invitations).length + count;

      // This allowed User count is all the Users the subscribed plan will have.
      const futureAllowedUserCount = currentInvCount + teamMemberCount;

      const existingInvitation = noCapFind(
        ({ status }, index) => index === key && !fp.isEmpty(status)
      )(invitations);

      // Only update the invitation count if the allowed User is in specified range.
      if (selectedPlan && futureAllowedUserCount >= selectedPlan.minQuantity &&
        futureAllowedUserCount <= selectedPlan.maxQuantity && !existingInvitation) {
        const maxInvExceeded = currentInvCount > maxAllowedInvitations;
        const exceededCount = currentInvCount - maxAllowedInvitations;
        let currentSubCount = currentSubQuantity;

        // If add more rows for invitations, subscription quantity cannot be less than
        // current subscription quantity. Since this allowed user count is based on current
        // displayed rows, which may or may not be reached the actual subscription quantity.
        if (count > 0) {
          currentSubCount = fp.max([futureAllowedUserCount, currentSubCount]);
        }

        if (count < 0) {
          currentSubCount = fp.min([futureAllowedUserCount, currentSubCount]);
        }

        updateInvitations(key, count, invitations, maxInvExceeded, exceededCount, currentSubCount);

        if (maxInvExceeded) {
          setExtraChargeState(selectedPlan, exceededCount);
        }
      }
    },
    invite: ({
      user,
      invitations,
      setValidInvitations,
      updateBusinessSubscription,
      business,
      currentSubQuantity,
      sendInvitations,
      goTo,
      postConfirmAction,
      existingPendingInvitations,
      inviteOption,
      csvFileInvitations
    }) => (e) => {
      const invitationsToSubmit = inviteOption === inviteType.Manual ? invitations : csvFileInvitations

      const businessAdminUserRole = fp.find(
        ({ role }) => role === userRoles.BusinessAdmin || role === userRoles.Manager
      )(user.currentUser.roleAssignments);

      if (businessAdminUserRole) {
        const validatedInvitations = fp.flow(
          fp.filter(({ status }) => !status),
          fp.map(
            ({
              email,
              ...inv
            }) => ({
              ...inv,
              email,
              invalidEmail: email !== '' && !validateEmail(email),
              hasExistingPendingInvite: fp.flow(
                fp.map(({ email }) => email),
                fp.includes(email)
              )(existingPendingInvitations)
            })
          )
        )(invitationsToSubmit);
        const validInvitations = fp.filter(isInvitationValid)(validatedInvitations);
        const invalidInvitation = fp.find(
          ({ invalidEmail, hasExistingPendingInvite }) => invalidEmail || hasExistingPendingInvite
        )(validatedInvitations);

        if (inviteOption === inviteType.Manual) {
          setValidInvitations(validatedInvitations);
        }

        if (!invalidInvitation) {
          // Call update the subscription if invitation slots have been added
          // or removed. Note that the subscription quantity will still be
          // updated even if the invitation slots have not been used.
          updateBusinessSubscription({
            businessId: businessAdminUserRole.businessId,
            subscription: {
              ...business.subscription,
              quantity: currentSubQuantity
            }
          });
          sendInvitations({
            businessId: businessAdminUserRole.businessId,
            invitations: validInvitations
          });

          if (postConfirmAction) {
            postConfirmAction();
          }
          else {
            goTo(routes.app.root);
          }
        }

        handleInvalidForm(e.target);
      }
    },
    initInvitations: ({
      business,
      currentSubscription,
      user,
      maxInitialInvitationRows,
      invitations,
      setInitInvitations,
      touchedSubscription,
      goTo,
      setTouchSubscription,
      getBusinessSubscription,
      invitationInited,
      displayExistingInvitations
    }) => () => {
      if (currentSubscription && currentSubscription.quantity &&
        !invitationInited && !business.loadingInvitations && !business.isFetchingTeamMembers) {
        const existingInvs = existingInvitations(pendingInvitations(business.invitations));
        const maxAllowedInvitations = fp.min([
          displayExistingInvitations ?
            maxAllowedInv(currentSubscription, business.teamMembers) :
            maxAllowedInv(currentSubscription, business.teamMembers) - existingInvs.length,
          maxInitialInvitationRows
        ]);
        const remainingAllowedInvitations = remainingAllowedInvitationCount(
          maxAllowedInvitations,
          displayExistingInvitations ?
            existingInvs :
            []
        );
        const remainingInvs = remainingInvitations(remainingAllowedInvitations);

        setInitInvitations(existingInvs.concat(remainingInvs), currentSubscription.quantity);
      }
      else if (!touchedSubscription) {
        const businessAdminUserRole = businessAdminRole(user.currentUser.roleAssignments);

        if (user.currentUser.id && !isBusinessAdmin(user.currentUser)) {
          goTo(routes.app.root);
        }

        if (businessAdminUserRole) {
          setTouchSubscription();
          getBusinessSubscription(businessAdminUserRole.businessId);
        }
      }
    },
    copyToClipboard: ({ textRef, copyInvitationLink }) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      textRef.select();
      document.execCommand('copy');
      e.target.focus();
      copyInvitationLink();
    }
  }),
  withHandlers(({
    submitForm: ({
      invite,
      inviteOption,
      goTo,
      showConfirmationModal,
      postConfirmAction,
      cancelAction
    }) => (e) => {
      if (inviteOption === inviteType.Manual) {
        invite(e)
      }
      else if (inviteOption === inviteType.BulkUpload) {
        showConfirmationModal(e)
      }
      else {
        if (postConfirmAction) {
          cancelAction();
        }
        else {
          goTo(routes.app.root);
        }
      }
    }
  })),
  branch(
    ({
      loadingInvitations,
      business: {
        isFetching,
        isFetchingTeamMembers
      }
    }) => loadingInvitations || isFetching || isFetchingTeamMembers,
    renderComponent(InivtationLoader)
  ),
  lifecycle({
    componentDidMount() {
      const {
        businessId,
        initInvitations,
        selectedPlan,
        getInvitationLink,
        getPlans
      } = this.props;
      initInvitations();
      if (!selectedPlan) {
        getPlans();
      }
      getInvitationLink(businessId)
    },
    componentDidUpdate() {
      const {
        remainingInvitationCount,
        updateInvitationCount,
        exceededCount,
        selectedPlan
      } = this.props;
      if (selectedPlan && remainingInvitationCount === 0 && (!exceededCount)) {
        updateInvitationCount(null, 1);
      }
    }
  }),
  branch(
    ({
      business: {
        subscription
      }
    }) => !subscription.id,
    renderComponent(NoValidSubscription)
  )
)(TeamInvitations);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/team-invitations.js